import { parseISO } from 'date-fns';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import omit from 'lodash/omit';
import reduce from 'lodash/reduce';

import { getString } from '.';
import { localeFormat, momentTz } from './date';
import { getWorkshopStatus, isNow } from './dateUtils';

// Group holding the items without any value for the grouped field
export const OTHERS_GROUP_ID = '__others__';

// A missing value, an empty string or an empty array means "no group"
function hasNoValue(value) {
  if (Array.isArray(value)) return value.length === 0;
  return value === undefined || value === null || value === '';
}

function toDate(value, withTime = false) {
  if (!value) return value;
  const defaultFormat = 'YYYY-MM-DD';
  const dateFormat = withTime ? `${defaultFormat} HH:mm` : defaultFormat;
  return momentTz(parseISO(value)).format(dateFormat);
}

export function generateWorkshopStatus(workshops, options = undefined) {
  return (workshops || []).map((workshop) => ({
    ...workshop,
    workshopStatus: getWorkshopStatus(workshop, options),
    isLive: options?.showLive
      ? isNow(workshop, { minutesBefore: 0, minutesAfter: 0, ...options })
      : false,
  }));
}

function groupByWithArrays(collection, iteratee) {
  function add(result, key, value) {
    if (hasOwnProperty.call(result, key)) {
      // eslint-disable-next-line no-param-reassign
      result[key].push(value);
    } else {
      // eslint-disable-next-line no-param-reassign
      result[key] = [value];
    }
  }
  let mapper = iteratee;
  if (typeof iteratee !== 'function') {
    mapper = (value) => get(value, iteratee);
  }
  return reduce(
    collection,
    (result, value) => {
      const key = mapper(value);
      if (Array.isArray(key)) {
        const keys = key.filter((k) => !hasNoValue(k));
        if (keys.length === 0) {
          add(result, OTHERS_GROUP_ID, value);
        } else {
          // eslint-disable-next-line no-restricted-syntax
          for (const k of keys) {
            add(result, k, value);
          }
        }
      } else {
        add(result, hasNoValue(key) ? OTHERS_GROUP_ID : key, value);
      }
      return result;
    },
    {},
  );
}

export function generateGroups(data, groupByConfig) {
  const {
    field,
    type,
    dateFormat = 'PPPP',
    withTime,
    options,
    showOthers = true,
    othersLabel,
  } = groupByConfig;

  // Items without value are gathered in a single "others" group, displayed last
  function othersGroups(items) {
    if (!showOthers || !items?.length) return [];
    return [
      {
        id: OTHERS_GROUP_ID,
        label: othersLabel || getString('common.others') || '',
        items,
      },
    ];
  }

  if (type === 'date' || field === 'startDate') {
    const groups = groupBy(data, (item) => {
      const value = item[field] || item?.sessions?.[0]?.[field];
      if (!value) return OTHERS_GROUP_ID;
      return toDate(value, withTime);
    });

    return [
      ...Object.keys(omit(groups, OTHERS_GROUP_ID))
        .sort()
        .map((key) => ({
          id: key,
          label: localeFormat(parseISO(key), dateFormat),
          items: groups[key],
        })),
      ...othersGroups(groups[OTHERS_GROUP_ID]),
    ];
  }

  // TODO: order keys ?
  const groups = groupByWithArrays(data, field);

  if (options && options.length > 0) {
    // Manual order/label
    const manualGroups = options
      .map(({ value, label, ...rest }) => ({
        id: value,
        label,
        ...rest,
        items: groups[value] || [],
      }))
      .filter((opt) => opt.items.length > 0);

    // The "others" group may be declared (and placed/labelled) in the options
    if (options.some(({ value }) => value === OTHERS_GROUP_ID)) return manualGroups;

    return [...manualGroups, ...othersGroups(groups[OTHERS_GROUP_ID])];
  }

  return [
    ...Object.keys(omit(groups, OTHERS_GROUP_ID)).map((key) => ({
      id: key,
      label: key,
      items: groups[key],
    })),
    ...othersGroups(groups[OTHERS_GROUP_ID]),
  ];
}
